
import React, {
    useState,
    useEffect,
    useCallback
} from "react";

import { Link } from "react-router-dom";

import { AdminSideMenu } from "../admin/AdminMenu";

import API_URL from "../../api/API";

import { useAdminAuthContext } from "../admin/useAdminAuthContext";


export const AdminManageProducts = () => {

    const { admin } = useAdminAuthContext();

    const [products, setProducts] = useState([]);

    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    const [statusFilter, setStatusFilter] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    const [isLoading, setIsLoading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const [exportingId, setExportingId] = useState(null);

    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");


    // ========================================
    // FETCH PRODUCTS
    // ========================================

    const fetchProducts = useCallback(async () => {

        if (!admin?.token) {
            setProducts([]);
            setIsLoading(false);
            return;
        }

        try {

            setIsLoading(true);
            setError("");

            const params = new URLSearchParams({
                page: String(currentPage),
                limit: "20",
                search: searchQuery,
                status: statusFilter
            });

            const response = await fetch(
                `${API_URL.ADMIN_PRODUCT_ALL}?${params.toString()}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${admin.token}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.error || "Failed to fetch products"
                );
            }

            setProducts(data.products || []);
            setTotal(data.total || 0);
            setTotalPages(
                Math.max(1, data.totalPages || 1)
            );

        } catch (err) {

            setError(err.message);

        } finally {

            setIsLoading(false);

        }

    }, [
        admin?.token,
        currentPage,
        searchQuery,
        statusFilter
    ]);


    useEffect(() => {

        fetchProducts();

    }, [fetchProducts]);


    // ========================================
    // SEARCH
    // ========================================

    const handleSearch = (event) => {

        event.preventDefault();

        setCurrentPage(1);
        setSearchQuery(searchInput.trim());
        setSuccessMessage("");

    };


    const handleClearSearch = () => {

        setSearchInput("");
        setSearchQuery("");
        setCurrentPage(1);
        setSuccessMessage("");

    };


    // ========================================
    // STATUS FILTER
    // ========================================

    const handleStatusChange = (event) => {

        setStatusFilter(event.target.value);
        setCurrentPage(1);
        setSuccessMessage("");

    };


    // ========================================
    // DELETE PRODUCT
    // ========================================

    const handleDelete = async (product) => {

        const productName =
            product.identity?.name || product.productKey;

        const confirmed = window.confirm(
            `Are you sure you want to delete "${productName}"?\n\nThis action cannot be undone.`
        );

        if (!confirmed) {
            return;
        }

        try {

            setDeletingId(product._id);
            setError("");
            setSuccessMessage("");

            const response = await fetch(
                API_URL.ADMIN_DELETE_PRODUCT + product._id,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${admin.token}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.error || "Failed to delete product"
                );
            }

            setSuccessMessage(
                "Product deleted successfully."
            );

            if (
                products.length === 1 &&
                currentPage > 1
            ) {

                setCurrentPage((page) => page - 1);

            } else {

                await fetchProducts();

            }

        } catch (err) {

            setError(err.message);

        } finally {

            setDeletingId(null);

        }

    };
    

    // ========================================
    // EXPORT PRODUCT AS TXT
    // ========================================

    const handleExport = async (product) => {

        if (!admin?.token || exportingId) {
            return;
        }

        let downloadUrl = null;

        try {

            setExportingId(product._id);
            setError("");
            setSuccessMessage("");

            const response = await fetch(
                API_URL.ADMIN_PRODUCT_EXPORT +
                product._id +
                "/export",
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${admin.token}`
                    }
                }
            );

            if (!response.ok) {

                let message = "Failed to export product";

                try {
                    const data = await response.json();
                    message = data.error || message;
                } catch (error) {
                    // Keep default error message.
                }

                throw new Error(message);
            }

            const blob = await response.blob();

            downloadUrl = window.URL.createObjectURL(blob);

            const link = document.createElement("a");

            link.href = downloadUrl;

            link.download =
                `${product.productKey || "product"}.txt`;

            document.body.appendChild(link);

            link.click();

            document.body.removeChild(link);

            setSuccessMessage(
                `Export ready for ${
                    product.identity?.name || product.productKey
                }.`
            );

        } catch (err) {

            setError(
                err.message || "Failed to export product"
            );

        } finally {

            if (downloadUrl) {
                window.URL.revokeObjectURL(downloadUrl);
            }

            setExportingId(null);

        }

    };


    // ========================================
    // STATUS DISPLAY
    // ========================================

    const renderStatus = (status) => {

        const statusStyles = {
            draft: "bg-gray-100 text-gray-700",
            review: "bg-yellow-100 text-yellow-800",
            approved: "bg-green-100 text-green-800"
        };

        const statusLabels = {
            draft: "Draft",
            review: "In Review",
            approved: "Approved"
        };

        return (

            <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                    statusStyles[status] || statusStyles.draft
                }`}
            >
                {statusLabels[status] || "Draft"}
            </span>

        );

    };


    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="flex min-h-screen bg-black">

            {/* FULL HEIGHT BLACK SIDE MENU */}

            <div className="bg-black self-stretch flex-shrink-0">
                <AdminSideMenu />
            </div>


            {/* MAIN CONTENT */}

            <div className="flex-1 min-w-0 bg-gray-100 p-6 pt-16 flex flex-col">

                {/* HEADER */}

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">

                    <div>

                        <h1 className="text-2xl font-bold text-gray-900">
                            Manage Products
                        </h1>

                        <p className="text-sm text-gray-500 mt-1">
                            Manage RAZON product information and knowledge.
                        </p>

                    </div>

                    <Link
                        to="/administrator/product/create"
                        className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 text-center"
                    >
                        + Add Product
                    </Link>

                </div>


                {/* SEARCH AND FILTERS */}

                <div className="bg-white border rounded-lg p-4 mb-5">

                    <div className="flex flex-col md:flex-row gap-3">

                        <form
                            onSubmit={handleSearch}
                            className="flex flex-1 gap-2"
                        >

                            <input
                                type="text"
                                placeholder="Search product name, code or category"
                                value={searchInput}
                                onChange={(event) =>
                                    setSearchInput(event.target.value)
                                }
                                className="flex-1 min-w-0 p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-300"
                            />

                            <button
                                type="submit"
                                className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700"
                            >
                                Search
                            </button>

                            {searchQuery && (

                                <button
                                    type="button"
                                    onClick={handleClearSearch}
                                    className="px-3 py-2 border rounded hover:bg-gray-100"
                                >
                                    Clear
                                </button>

                            )}

                        </form>


                        <select
                            value={statusFilter}
                            onChange={handleStatusChange}
                            className="p-2 border border-gray-300 rounded bg-white"
                        >

                            <option value="">All Statuses</option>
                            <option value="draft">Draft</option>
                            <option value="review">In Review</option>
                            <option value="approved">Approved</option>

                        </select>

                    </div>

                </div>


                {/* ERROR MESSAGE */}

                {error && (

                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                        {error}
                    </div>

                )}


                {/* SUCCESS MESSAGE */}

                {successMessage && (

                    <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded">
                        {successMessage}
                    </div>

                )}


                {/* PRODUCT TABLE */}

                <div className="bg-white border rounded-lg shadow-sm overflow-x-auto">

                    <table className="w-full text-sm">

                        <thead className="bg-gray-200">

                            <tr>

                                <th className="px-4 py-3 text-left">
                                    Product
                                </th>

                                <th className="px-4 py-3 text-left">
                                    Code
                                </th>

                                <th className="px-4 py-3 text-left">
                                    Categories
                                </th>

                                <th className="px-4 py-3 text-left">
                                    Status
                                </th>

                                <th className="px-4 py-3 text-left">
                                    Active
                                </th>

                                <th className="px-4 py-3 text-left">
                                    Updated
                                </th>

                                <th className="px-4 py-3 text-left">
                                    Actions
                                </th>

                            </tr>

                        </thead>

                        <tbody>

                            {isLoading ? (

                                <tr>

                                    <td
                                        colSpan={7}
                                        className="text-center py-10 text-blue-600"
                                    >
                                        Loading products...
                                    </td>

                                </tr>

                            ) : products.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan={7}
                                        className="text-center py-12 text-gray-500"
                                    >
                                        No products found.
                                    </td>

                                </tr>

                            ) : (

                                products.map((product, index) => (

                                    <tr
                                        key={product._id}
                                        className={`border-t border-gray-200 ${
                                            index % 2 === 0
                                                ? "bg-white"
                                                : "bg-gray-50"
                                        } hover:bg-gray-100`}
                                    >

                                        {/* PRODUCT */}

                                        <td className="px-4 py-3">

                                            <div className="font-semibold text-gray-900">
                                                {product.identity?.name || "-"}
                                            </div>

                                            <div className="text-xs text-gray-500 mt-1">
                                                {product.productKey}
                                            </div>

                                        </td>


                                        {/* CODE */}

                                        <td className="px-4 py-3">
                                            {product.identity?.code || "-"}
                                        </td>


                                        {/* CATEGORIES */}

                                        <td className="px-4 py-3">

                                            <div className="flex flex-wrap gap-1">

                                                {product.identity?.categories?.length > 0
                                                    ? product.identity.categories.map(
                                                        (category, categoryIndex) => (

                                                            <span
                                                                key={categoryIndex}
                                                                className="px-2 py-1 bg-gray-100 border rounded text-xs"
                                                            >
                                                                {category.name}
                                                            </span>

                                                        )
                                                    )
                                                    : "-"}

                                            </div>

                                        </td>


                                        {/* STATUS */}

                                        <td className="px-4 py-3">
                                            {renderStatus(
                                                product.approval?.status
                                            )}
                                        </td>


                                        {/* ACTIVE */}

                                        <td className="px-4 py-3">

                                            <span
                                                className={
                                                    product.isActive
                                                        ? "text-green-700 font-medium"
                                                        : "text-gray-500"
                                                }
                                            >
                                                {product.isActive
                                                    ? "Yes"
                                                    : "No"}
                                            </span>

                                        </td>


                                        {/* UPDATED */}

                                        <td className="px-4 py-3 whitespace-nowrap">

                                            {product.updatedAt
                                                ? new Date(
                                                    product.updatedAt
                                                ).toLocaleDateString()
                                                : "-"}

                                        </td>


                                        {/* ACTIONS */}

                                        <td className="px-4 py-3">

                                           
<div className="flex flex-wrap items-center gap-2">

<Link
    to={`/administrator/product/edit/${product._id}`}
    className="px-3 py-1 bg-gray-800 text-white rounded hover:bg-gray-700"
>
    Edit
</Link>

<button
    type="button"
    onClick={() => handleExport(product)}
    disabled={exportingId !== null}
    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
>
    {exportingId === product._id
        ? "Exporting..."
        : "Export TXT"}
</button>

<button
    type="button"
    onClick={() => handleDelete(product)}
    disabled={deletingId === product._id}
    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
>
    {deletingId === product._id
        ? "Deleting..."
        : "Delete"}
</button>

</div>


                                        </td>

                                    </tr>

                                ))

                            )}

                        </tbody>

                    </table>

                </div>


                {/* PAGINATION */}

                <div className="flex justify-between items-center gap-4 mt-5">

                    <button
                        type="button"
                        onClick={() =>
                            setCurrentPage((page) =>
                                Math.max(1, page - 1)
                            )
                        }
                        disabled={
                            currentPage === 1 ||
                            isLoading
                        }
                        className="px-4 py-2 rounded bg-gray-800 text-white hover:bg-gray-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                    >
                        Previous
                    </button>

                    <p className="text-sm text-gray-600 text-center">

                        Page {currentPage} of {totalPages}

                        <span className="hidden sm:inline">
                            {" "}· {total} products
                        </span>

                    </p>

                    <button
                        type="button"
                        onClick={() =>
                            setCurrentPage((page) =>
                                Math.min(totalPages, page + 1)
                            )
                        }
                        disabled={
                            currentPage >= totalPages ||
                            isLoading
                        }
                        className="px-4 py-2 rounded bg-gray-800 text-white hover:bg-gray-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>

                </div>

            </div>

        </div>

    );

};

export default AdminManageProducts;
